import React, { useState, useEffect } from 'react'
import { compose, graphql } from 'react-apollo'
import PropTypes from 'prop-types'

// Lodash
import _map from 'lodash/map'
import _get from 'lodash/get'
import _isNil from 'lodash/isNil'
import _isEmpty from 'lodash/isEmpty'

// Services
import { GetNotesByUserId } from '@sukiai/gql/app'

// Components
import Task from './components/task'
import KebabMenu from './components/kebabMenu'

// Helpers
import loSto from '../../config/loSto'
import { getCompletedNotesCount } from '../../helpers/notes'
import { setNotesCountTask } from '../../helpers/gettingStarted.helpers'
import {
  isGettingStartedCardDisabled,
  addCustomRenderersToOobeTasks
} from './helpers/gettingStarted'

// Utils
import pubSub from '../../lib/util/pubSub'

// Constants
import { EMPTY_OBJECT, MAX_NUMBER } from '../../constants/base.constants'
import { TITLE, SUB_TITLE } from './constants/gettingStarted.general'
import { INITIAL_GETTING_STARTED_DETAILS } from '../../constants/gettingStarted/general'
import { LO_STO, FETCH_POLICY } from '../../lib/constants'
import {
  OOBE_TASKS_UPDATE,
  OOBE_LAYOUT_UPDATE
} from '../../constants/pubSub.events'

// Styles
import styles from './gettingStarted.module.scss'

const renderTask = (taskDetails, index) => (
  <Task key={index} {...taskDetails} />
)

const GettingStarted = ({ notesCount, fetchingNotesCount }) => {
  const [oobeTasks, setOOBETasks] = useState(EMPTY_OBJECT)
  const [disabled, setDisabled] = useState(true)

  const { enabled, tasks } = oobeTasks

  const fetchTaskDetailsFromStore = () => {
    let oobeTasksFromStore = loSto.get(LO_STO.OOBE_TASKS)
    if (_isEmpty(oobeTasksFromStore)) {
      loSto.set(LO_STO.OOBE_TASKS, INITIAL_GETTING_STARTED_DETAILS)
      oobeTasksFromStore = INITIAL_GETTING_STARTED_DETAILS
    }
    const oobeTasksToDisplay =
      addCustomRenderersToOobeTasks(oobeTasksFromStore)
    setOOBETasks(oobeTasksToDisplay)
  }

  const handleRemoveGettingStarted = () => {
    loSto.set(LO_STO.OOBE_TASKS, {
      ...oobeTasks,
      enabled: false
    })
    fetchTaskDetailsFromStore()
  }

  useEffect(() => {
    fetchTaskDetailsFromStore()
    const subscription = pubSub.subscribe(
      OOBE_TASKS_UPDATE,
      fetchTaskDetailsFromStore
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    setNotesCountTask(notesCount)
    const disabled = isGettingStartedCardDisabled(
      enabled,
      fetchingNotesCount,
      notesCount
    )
    setDisabled(disabled)
  }, [notesCount, fetchingNotesCount, enabled])

  useEffect(() => {
    pubSub.publish(OOBE_LAYOUT_UPDATE, {
      disabled
    })
  }, [disabled])

  if (disabled) {
    return null
  }

  return (
    <div className={styles.outerContainer}>
      <div className={styles.container}>
        <div className={styles.heading}>
          <div className={styles.titleRow}>
            <div className={styles.title}>{TITLE}</div>
            <KebabMenu onClick={handleRemoveGettingStarted} />
          </div>
          <div className={styles.subTitle}>{SUB_TITLE}</div>
        </div>

        <div className={styles.content}>{_map(tasks, renderTask)}</div>
      </div>
    </div>
  )
}

GettingStarted.propTypes = {
  notesCount: PropTypes.number,
  fetchingNotesCount: PropTypes.bool
}

GettingStarted.defaultProps = {
  notesCount: MAX_NUMBER,
  fetchingNotesCount: true
}

const mapNotesCountToProps = (response) => {
  const notes = _get(response, 'data.allNotes.results')
  return {
    notesCount: getCompletedNotesCount(notes),
    fetchingNotesCount: _isNil(notes)
  }
}

export default compose(
  graphql(GetNotesByUserId, {
    options: () => ({
      variables: {
        userId: loSto.get(LO_STO.USER_ID)
      }
    }),
    fetchPolicy: FETCH_POLICY.NETWORK_ONLY,
    props: mapNotesCountToProps
  })
)(GettingStarted)
